import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Spinner, Button, Image } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import moment from 'moment';
import { showCourseRequest } from '../../../requests/courses';
import { createEmployeeCourseRequest, deleteEmployeeCourseRequest } from '../../../requests/employeeCourses';
import { sendAlert } from '../../../actions/utils';
import defaultBanner from '../../../assets/images/bg-default.png';
import './style.scss';

const CourseModalShow = ({ courseId, onActionSuccess }) => {
  const [course, setCourse] = useState(null);
  const [onRequest, setOnRequest] = useState(true);
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const employeeId = user.employeeCard?.id;

  const loadCourseData = useCallback(() => {
    setOnRequest(true);
    showCourseRequest(courseId, {
      dispatch,
      successCallback: res => {
        setCourse(camelCaseRecursive(res.data));
      },
      callback: () => setOnRequest(false)
    });
  }, [courseId, dispatch]);

  useEffect(() => {
    loadCourseData();
  }, [loadCourseData]);

  if (onRequest && !course) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!course) return null;

  const outDate = moment().format('DD/MM/YYYY') > course.limitDate;
  const isAdmitted = course?.currentEmployeeCourse?.admission === 'approved';
  const isPending = course?.currentEmployeeCourse?.admission === 'pending';

  const handleInscription = () => {
    setOnRequest(true);
    createEmployeeCourseRequest(course.id, {
      dispatch,
      params: { employee_course: { employee_id: employeeId } },
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Inscrito con éxito' }));
        loadCourseData();
        if (onActionSuccess) onActionSuccess();
      },
      callback: () => setOnRequest(false)
    });
  };

  const handleCancelInscription = () => {
    setOnRequest(true);
    deleteEmployeeCourseRequest(course.id, course?.currentEmployeeCourse?.id, {
      dispatch,
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Inscripción cancelada' }));
        loadCourseData();
        if (onActionSuccess) onActionSuccess();
      },
      callback: () => setOnRequest(false)
    });
  };

  let cancelLabel = 'RECHAZADO';
  if (isAdmitted) cancelLabel = 'INSCRITO';
  else if (isPending) cancelLabel = 'CANCELAR INSCRIPCIÓN';

  return (
    <div className={`course-modal-show ${onRequest ? 'bg-opacity' : ''}`}>
      <Row className="mb-3">
        <Col xs={8}>
          <p className="mb-0 text-uppercase font-weight-bold">{course.name}</p>
        </Col>
        <Col xs={4} className="text-right">
          {course.discountAvailability > 0 ? (
            <div className="availability-badge">{course.discountAvailability} Cupos Disponibles</div>
          ) : (
            <div className="availability-badge">Sin cupos</div>
          )}
          <div className="limit-date">Límite: {course.limitDate}</div>
        </Col>
      </Row>

      <div className="position-relative">
        <Image
          src={course.courseImage?.fileUrl || defaultBanner}
          alt={course.name}
          className="banner-course shadow-sm"
          fluid
        />
        {onRequest && (
          <div
            className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ top: 0, left: 0, background: 'rgba(255,255,255,0.5)' }}
          >
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        )}
      </div>

      <Row className="mt-4">
        <Col md={12}>
          <div className="course-info-label">Tema:</div>
          <div className="course-info-value">{course.theme || 'N/A'}</div>

          <div className="course-info-label">Descripción:</div>
          <div className="course-info-value text-justify">{course.description || 'Sin descripción'}</div>
        </Col>
      </Row>

      <hr />

      <Row>
        <Col md={6}>
          <div className="course-info-label">Tipo de Actividad:</div>
          <div className="course-info-value">Curso</div>

          <div className="course-info-label">Fecha de Inicio:</div>
          <div className="course-info-value">{course.startDate}</div>

          <div className="course-info-label">Hora Inicio:</div>
          <div className="course-info-value">{course.startTime}</div>

          <div className="course-info-label">Duración:</div>
          <div className="course-info-value">{course.duration || 'N/A'}</div>

          <div className="course-info-label">Dirección:</div>
          <div className="course-info-value">{course.fullAddress || 'No aplica'}</div>
        </Col>
        <Col md={6}>
          <div className="course-info-label">Modalidad:</div>
          <div className="course-info-value">{course.translatedCourseModality}</div>

          <div className="course-info-label">Fecha de Término:</div>
          <div className="course-info-value">{course.endDate}</div>

          <div className="course-info-label">Hora Término:</div>
          <div className="course-info-value">{course.endTime}</div>

          <div className="course-info-label">Días Clases:</div>
          <div className="course-info-value">{course.journeyDistribution || 'N/A'}</div>

          <div className="course-info-label">Link:</div>
          <div className="course-info-value">{course.link || 'No aplica'}</div>
        </Col>
      </Row>

      <Row className="mt-4 mb-3 justify-content-center">
        <Col xs="auto">
          {course.signUp !== 'not_sign_up' ? (
            <Button
              variant="danger"
              onClick={handleCancelInscription}
              disabled={onRequest || course.status !== 'published' || !isPending}
            >
              {cancelLabel}
            </Button>
          ) : (
            <Button
              variant="success"
              className="btn-green-pw"
              onClick={handleInscription}
              disabled={onRequest || course.status !== 'published' || course.discountAvailability <= 0 || outDate}
            >
              {outDate ? 'FUERA DE PLAZO' : 'POSTULARME'}
            </Button>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CourseModalShow;
